import { useTranslation } from '@thoughtspot/i18n';
import { jsonConstants } from '@thoughtspot/json-constants';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { MutedAlert } from '@thoughtspot/radiant-react/widgets/alert/muted/muted-alert';
import { Checkbox, useCheckboxState, } from '@thoughtspot/radiant-react/widgets/checkbox';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Horizontal, Vertical, View, } from '@thoughtspot/radiant-react/widgets/layout';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { TextArea } from '@thoughtspot/radiant-react/widgets/text-area';
import { Toggle } from '@thoughtspot/radiant-react/widgets/toggle';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { isSpotterExperienceEnabled, isStrictPrivilegeManagementEnabled, } from '@thoughtspot/session-service';
import cx from 'classnames';
import _ from 'lodash';
import React, { createRef, Fragment, useCallback, useEffect, useRef, useState, } from 'react';
import { getInputLabel, getInputLabelColor, hasCreateEditInputError, isVisible, ON_CHANGE_DELAY, renderFormFieldValue, renderItemForReviewPrivilege, TranslationsKeys, } from '../util';
import styles from './create-edit-modal.module.scss';
const FORM_INPUT_ITEM = {
    roleName: 'roleName',
    roleDescription: 'roleDescription',
};
export const CreateEditForm = ({ privilegeIds = [], preSelectedPrivilegeIds = [], allCheckboxes = [], roleData = {}, message, isEditMode, isModalOpened, setShowError, closeModal, onSuccess, sourceData, filterPrivileges, }) => {
    const { t } = useTranslation();
    const getTranslations = (key, args) => t(`${key}`, args);
    const formData = useRef({
        name: '',
        description: t(TranslationsKeys.None),
    });
    const [isReviewSelection, setIsReviewSelection] = useState(true);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(message);
    const [name, setName] = useState(roleData.Data?.name || '');
    const [description, setDescription] = useState(roleData.Data?.description || '');
    const [hasInputError, setHasInputError] = useState({
        isNameRequired: false,
        isNameLong: false,
        isDescriptionLong: false,
        isUntouched: true,
    });
    const { checkedItems, handleChange, setCheckedItems } = useCheckboxState(privilegeIds, preSelectedPrivilegeIds);
    const componentRefs = useRef([]);
    const generateRefs = useCallback(() => {
        const count = allCheckboxes.length + 1;
        componentRefs.current = Array(count)
            .fill(null)
            .map((_, i) => componentRefs.current[i] ?? createRef());
    }, []);
    const handleInputChange = _.debounce((event) => {
        const { id, value } = event.target;
        setHasInputError({ ...hasInputError, isUntouched: false });
        if (FORM_INPUT_ITEM.roleName === id) {
            setName(value);
        }
        if (FORM_INPUT_ITEM.roleDescription === id) {
            setDescription(value);
        }
    }, ON_CHANGE_DELAY);
    useEffect(() => {
        if (!isError && !_.isEmpty(message)) {
            setIsError(true);
            setErrorMessage(message);
        }
    }, [message, isError]);
    useEffect(() => {
        if (!hasInputError.isUntouched) {
            setHasInputError(hasCreateEditInputError(hasInputError, name, description));
        }
    }, [name, description]);
    const submitForm = useCallback(() => {
        const { isNameRequired, isNameLong, isDescriptionLong } = hasInputError;
        if (isNameRequired || isNameLong || isDescriptionLong) {
            return;
        }
        const newFormData = {};
        newFormData.name = name || '';
        newFormData.description = description || '';
        newFormData.privileges = JSON.stringify([...checkedItems]);
        onSuccess(newFormData);
    }, [checkedItems, name, description]);
    const onDismissModal = () => {
        closeModal();
    };
    const onConfirmModal = () => {
        submitForm();
    };
    const onBannerClose = useCallback(() => {
        setShowError(null);
        setIsError(false);
        setErrorMessage(null);
    }, []);
    const onReviewSelection = () => {
        const roleName = name.trim();
        formData.current = {
            name: '',
            description: t(TranslationsKeys.None),
        };
        if (roleName) {
            formData.current.name = roleName;
        }
        if (description) {
            formData.current.description = description;
        }
        const { isNameRequired, isNameLong, isDescriptionLong } = hasInputError;
        if (!roleName || isNameRequired || isNameLong || isDescriptionLong) {
            setHasInputError(hasCreateEditInputError(hasInputError, roleName, description));
            componentRefs.current[0].current.scrollIntoView({
                behavior: 'smooth',
            });
            return;
        }
        if (isError || !_.isEmpty(errorMessage)) {
            setIsError(false);
            setErrorMessage(null);
        }
        setIsReviewSelection(false);
    };
    const onBackModal = () => {
        setIsReviewSelection(true);
    };
    const getReviewSelectionOrConfirmButton = () => {
        if (isReviewSelection) {
            return {
                text: t(TranslationsKeys.REVIEW_SELECTION),
                onConfirm: onReviewSelection,
            };
        }
        return {
            text: t(TranslationsKeys.SAVE),
            onConfirm: onConfirmModal,
        };
    };
    const getModalTitle = (title) => {
        if (!isReviewSelection) {
            return t(TranslationsKeys.REVIEW_SELECTION);
        }
        return t(TranslationsKeys[title]);
    };
    const modalProps = {
        size: ModalSize.MEDIUM,
        title: isEditMode
            ? getModalTitle('MODAL_EDIT_TITLE')
            : getModalTitle('MODAL_ADD_TITLE'),
        onDismiss: onDismissModal,
        isOpen: isModalOpened,
        confirmButton: getReviewSelectionOrConfirmButton(),
        denyButton: !isReviewSelection
            ? {
                text: t(TranslationsKeys.CANCEL),
                onDeny: onDismissModal,
            }
            : null,
        dismissButton: !isReviewSelection
            ? {
                text: t(TranslationsKeys.BACK),
                onClick: onBackModal,
            }
            : {
                text: t(TranslationsKeys.CANCEL),
            },
        className: styles.modalProps,
        contentClassName: styles.modalContentClassName,
        customHeaderClassName: styles.customHeader,
        customFooterClassName: styles.customFooter,
        autoScrollContent: false,
    };
    const showSelectedByGroup = (checked, groupData) => {
        const filterChecked = groupData.privileges.filter((item) => checkedItems.has(item));
        const filterGroup = sourceData.filter((item) => item.groupName === groupData.groupName);
        if (!filterChecked.length ||
            filterGroup[0].privileges.length === filterChecked.length) {
            return;
        }
        const checkboxData = _.cloneDeep(sourceData);
        const updatedData = checkboxData.map((item, idx) => {
            let privileges = item.privileges;
            if (checked && item.groupName === groupData.groupName) {
                privileges = item.privileges.filter((item) => checkedItems.has(item));
            }
            else if (item.groupName !== groupData.groupName) {
                privileges = [...allCheckboxes[idx].privileges];
            }
            else {
                privileges = [...item.privileges];
            }
            return {
                ...item,
                privileges,
            };
        });
        filterPrivileges(updatedData);
    };
    const selectAllOrClearByGroup = (groupData, isClear) => {
        let groupItems = [...groupData.privileges];
        if (isStrictPrivilegeManagementEnabled()) {
            groupItems = groupItems.filter((item) => isVisible(item));
        }
        if (isClear) {
            const checkboxData = _.cloneDeep(sourceData);
            const filterGroup = checkboxData.filter((item) => item.groupName === groupData.groupName);
            if (filterGroup[0].privileges.length !== groupItems.length) {
                groupItems = [...filterGroup[0].privileges];
                if (isStrictPrivilegeManagementEnabled()) {
                    groupItems = groupItems.filter((item) => isVisible(item));
                }
                showSelectedByGroup(false, groupData);
            }
        }
        setCheckedItems((prevState) => {
            const newState = new Set(prevState);
            groupItems.forEach(idx => {
                if (isClear ||
                    (!isClear &&
                        groupItems.every((item) => checkedItems.has(item)))) {
                    newState.delete(idx);
                }
                else {
                    newState.add(idx);
                }
            });
            return newState;
        });
    };
    const autoCheckUncheckTrustedAuthAndDeveloperPrivilege = (e, item) => {
        if (item === jsonConstants.privilegeType.CONTROL_TRUSTED_AUTH) {
            handleChange(e, jsonConstants.privilegeType.DEVELOPER);
        }
        if (item === jsonConstants.privilegeType.DEVELOPER &&
            !e.target.checked) {
            handleChange(e, jsonConstants.privilegeType.CONTROL_TRUSTED_AUTH);
        }
    };
    const renderPrivileges = (list) => {
        return list.map((record) => {
            const isSelectAllGroupCheckboxChecked = record.privileges.length > 0 &&
                record.privileges.every((data) => checkedItems.has(data));
            const groupItems = [...record.privileges];
            const assigned = groupItems.filter((item) => checkedItems.has(item));
            return (React.createElement(Fragment, { key: record.groupName },
                React.createElement(Typography, { variant: "content-label-subhead", overrideTag: "div", className: styles.sectionTitle }, getTranslations(`tennablePrivilegesLabels.${record.groupName}`)),
                React.createElement(Horizontal, { spacing: "c", className: styles.rolesPrivilegesTopContainer },
                    React.createElement(Horizontal, { className: styles.labelFontRegularSize },
                        t(TranslationsKeys.PRIVILEGES),
                        " (",
                        assigned.length,
                        ")"),
                    React.createElement(Horizontal, { hAlignContent: "right" },
                        React.createElement(Horizontal, { tabIndex: 0, onKeyDown: e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    selectAllOrClearByGroup(record, false);
                                }
                            } },
                            React.createElement(Typography, { variant: "p", overrideTag: "div", color: Colors.accent, className: styles.privilegeHeaderActionLink, "data-testid": `selectAll${record.groupName}`, onClick: () => {
                                    selectAllOrClearByGroup(record, false);
                                } }, t(TranslationsKeys.SELECT_ALL))),
                        React.createElement(Divider, { vertical: true, className: styles.privilegeHeaderActionLinkDivider }),
                        React.createElement(Horizontal, { tabIndex: 0, onKeyDown: e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    selectAllOrClearByGroup(record, true);
                                }
                            } },
                            React.createElement(Typography, { variant: "p", overrideTag: "div", color: Colors.accent, className: styles.privilegeHeaderActionLink, "data-testid": `clearAll${record.groupName}`, onClick: () => {
                                    selectAllOrClearByGroup(record, true);
                                } }, t(TranslationsKeys.CLEAR))))),
                React.createElement(Vertical, { className: styles.rolesPrivilegesContainer },
                    React.createElement(Vertical, { className: styles.childPrivileges, "data-testid": `childPrivileges-${record.groupName}` }, record.privileges.map((item) => (React.createElement(Checkbox, { id: item, key: item, label: getTranslations(item ===
                            'PREVIEW_THOUGHTSPOT_SAGE' &&
                            isSpotterExperienceEnabled()
                            ? TranslationsKeys.PREVIEW_THOUGHTSPOT_SPOTTER
                            : `tennablePrivilegesLabels.${item}`), checked: checkedItems.has(item), disabled: isStrictPrivilegeManagementEnabled() &&
                            !isVisible(item), isList: true, onChange: e => {
                            handleChange(e, item);
                            autoCheckUncheckTrustedAuthAndDeveloperPrivilege(e, item);
                        } })))),
                    React.createElement(Vertical, { className: styles.rolesPrivilegesShowSelectedContainer },
                        React.createElement(Toggle, { checked: isSelectAllGroupCheckboxChecked, onChange: (e) => {
                                showSelectedByGroup(e.target.checked, record);
                            }, labelPosition: "right", "data-testid": `showSelected${record.groupName}`, label: t(TranslationsKeys.SHOW_SELECTED) })))));
        });
    };
    const renderPrivilegeGroupMenu = (componentRefs, records) => {
        const scrollToComponent = (ref) => {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        };
        return (React.createElement(Vertical, { className: styles.leftPanel }, componentRefs.map((ref, index) => (React.createElement(Horizontal, { key: index === 0
                ? 'roleDescLeftPanelItem'
                : records[index - 1].groupName, className: styles.leftPanelHeader, vAlignContent: "center" },
            React.createElement("button", { onClick: () => scrollToComponent(ref), type: "button", "data-testid": `headerLink-${records[index]}`, onKeyDown: e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        scrollToComponent(ref);
                    }
                }, className: styles.leftPanelButton },
                React.createElement(Typography, { variant: "p", overrideTag: "div", "data-testid": index === 0
                        ? 'headerLink-roleDescription'
                        : `headerLink-${records[index - 1].groupName}` }, index === 0
                    ? t(TranslationsKeys.ROLE_DESCRIPTION_TITLE)
                    : getTranslations(`tennablePrivilegesLabels.${records[index - 1].groupName}`))))))));
    };
    const renderPrivilegesGroupContent = (ref, index) => {
        const record = allCheckboxes[index];
        return (React.createElement("div", { ref: ref, className: cx(styles.rightPanelItem, styles.rightPanelCustomItem), id: `section-${record.groupName}` }, renderPrivileges([record])));
    };
    const renderRoleDescContent = (ref) => {
        return (React.createElement("div", { ref: ref, className: styles.rightPanelItem, id: "section-roleDescContent" },
            React.createElement(Typography, { variant: "content-label-subhead", overrideTag: "div", className: styles.roleDescContentTitle }, t(TranslationsKeys.ROLE_DESCRIPTION_TITLE)),
            React.createElement("div", { className: styles.rolesModalForm },
                React.createElement(Typography, { variant: "p", className: cx(styles.formFieldBottomMargin, styles.labelFontRegularSize), overrideTag: "div", color: getInputLabelColor(hasInputError) && Colors.failure }, getInputLabel(hasInputError, t)),
                React.createElement(Input.Text, { ariaLabel: t(TranslationsKeys.ROLE_NAME_DESCRIPTION_LABEL), value: name, hasError: hasInputError.isNameRequired ||
                        hasInputError.isNameLong, id: FORM_INPUT_ITEM.roleName, "data-testid": FORM_INPUT_ITEM.roleName, className: styles.formInputFieldBottomMargin, onChange: handleInputChange }),
                React.createElement(TextArea, { ariaLabel: t(TranslationsKeys.ROLE_NAME_DESCRIPTION_LABEL), value: description, id: FORM_INPUT_ITEM.roleDescription, onChange: handleInputChange, hasError: hasInputError.isDescriptionLong, className: styles.formTextareaBottomMargin, "data-testid": FORM_INPUT_ITEM.roleDescription }))));
    };
    const renderReviewPrivilegeItems = (group, preSelectedPrivilegeIdsInMap) => {
        const existingSelectedPrivileges = [];
        let newlyAddedPrivileges = group.privileges;
        let removedPrivileges = [];
        let isOnlyRemove = false;
        let isMixedRemove = false;
        if (isEditMode) {
            const originalPrivileges = allCheckboxes.filter((record) => record.groupName === group.groupName);
            newlyAddedPrivileges = [];
            group.privileges.forEach((data) => {
                if (preSelectedPrivilegeIdsInMap.has(data)) {
                    existingSelectedPrivileges.push(data);
                }
                else {
                    newlyAddedPrivileges.push(data);
                }
            });
            const existingSelectedPrivilegesMap = new Set(existingSelectedPrivileges);
            const previouslySelected = originalPrivileges[0].privileges.filter((data) => preSelectedPrivilegeIdsInMap.has(data));
            removedPrivileges = previouslySelected.filter((data) => !existingSelectedPrivilegesMap.has(data));
            isOnlyRemove =
                _.isEmpty(newlyAddedPrivileges) &&
                    _.isEmpty(existingSelectedPrivileges) &&
                    !_.isEmpty(removedPrivileges);
            isMixedRemove = !_.isEmpty(removedPrivileges);
        }
        const newlyAddedPrivilegesLength = newlyAddedPrivileges.length;
        const removedPrivilegesLength = removedPrivileges.length;
        const existingSelectedPrivilegesLength = existingSelectedPrivileges.length;
        return (React.createElement(Horizontal, { className: styles.reviewPrivilegeMainContainer },
            !isOnlyRemove && (React.createElement(Vertical, { className: styles.reviewPrivilegeLeftContainer },
                newlyAddedPrivileges.map((privilege, idx) => renderItemForReviewPrivilege(privilege, cx(styles.reviewPrivilegeItem, {
                    [styles.firstItemTopBorderRadius]: idx === 0,
                    [styles.lastItemTopBorderRadius]: idx ===
                        newlyAddedPrivilegesLength - 1,
                }), IconID.CHECKMARK)),
                existingSelectedPrivileges.map((privilege, idx) => renderItemForReviewPrivilege(privilege, cx(styles.reviewExitingPrivilegeItem, {
                    [styles.firstItemTopBorderRadius]: idx === 0,
                    [styles.lastItemTopBorderRadius]: idx ===
                        existingSelectedPrivilegesLength -
                            1,
                }), IconID.CIRCLE, styles.reviewIconSize)))),
            React.createElement(Vertical, { className: styles.reviewPrivilegeRightContainer }, isMixedRemove &&
                removedPrivileges.map((privilege, idx) => renderItemForReviewPrivilege(privilege, cx(styles.reviewPrivilegeItem, {
                    [styles.firstItemTopBorderRadius]: idx === 0,
                    [styles.lastItemTopBorderRadius]: idx === removedPrivilegesLength - 1,
                }), IconID.CROSS)))));
    };
    const reviewPrivilegeList = (privilegeGroup, preSelectedPrivilegeIdsInMap) => {
        return (React.createElement(Vertical, { className: styles.reviewPrivilegeGroupContainer }, privilegeGroup.map((group) => (React.createElement(Vertical, { key: group.groupName },
            React.createElement(Vertical, null,
                React.createElement(Typography, { variant: "p-bold", overrideTag: "div" },
                    getTranslations(`tennablePrivilegesLabels.${group.groupName}`),
                    ":")),
            renderReviewPrivilegeItems(group, preSelectedPrivilegeIdsInMap))))));
    };
    const renderPrivilegeListReview = useCallback(() => {
        const preSelectedPrivilegeIdsInMap = new Set(preSelectedPrivilegeIds);
        const filtered = allCheckboxes.reduce((acc, obj) => {
            const items = obj.privileges;
            const selectedItems = items.filter((item) => checkedItems.has(item));
            if (selectedItems.length) {
                acc.push({ ...obj, privileges: selectedItems });
            }
            if (!selectedItems.length &&
                items.some((item) => preSelectedPrivilegeIdsInMap.has(item))) {
                acc.push({ ...obj, privileges: [] });
            }
            return acc;
        }, []);
        return reviewPrivilegeList(filtered, preSelectedPrivilegeIdsInMap);
    }, [preSelectedPrivilegeIds, checkedItems]);
    const renderReviewScreen = () => {
        if (!isReviewSelection) {
            const { name, description: desc } = formData.current;
            const isRoleNameUpdated = roleData.Data?.name !== name;
            const isRoleDescriptionUpdated = roleData.Data?.description !== description;
            return (React.createElement(Vertical, { className: styles.reviewScreen },
                renderFormFieldValue(t(TranslationsKeys.MODAL_FORM_NAME), isRoleNameUpdated, styles.reviewRoleName, name),
                renderFormFieldValue(t(TranslationsKeys.MODAL_FORM_DESCRIPTION), isRoleDescriptionUpdated, styles.reviewRoleName, desc),
                checkedItems.size ||
                    (isEditMode && preSelectedPrivilegeIds.length) ? (renderPrivilegeListReview()) : (React.createElement(Vertical, { hAlignContent: "center", className: styles.noPrivilegesInReview },
                    React.createElement(MutedAlert, { imageVisible: true, size: "small", title: t(TranslationsKeys.REVIEW_MODAL_NO_PRIVILEGE_MSG1), description: t(TranslationsKeys.REVIEW_MODAL_NO_PRIVILEGE_MSG2), className: styles.reviewMutedAlertContainer })))));
        }
        return null;
    };
    generateRefs();
    return (React.createElement(Modal, { ...modalProps },
        React.createElement(Vertical, { className: styles.modalContent, hAlignContent: "stretch", grow: true, "data-testid": "role-create-edit-container-modal" },
            React.createElement(Alert.Banner, { message: errorMessage, isVisible: isError, type: BannerAlertType.ERROR, className: styles.alertBanner, onClose: onBannerClose }),
            React.createElement("div", { "data-testid": "rolesModal_add" },
                React.createElement(Horizontal, { className: cx(styles.mainContent, {
                        [styles.hideAddEdit]: !isReviewSelection,
                    }), vAlignContent: "stretch" },
                    React.createElement(View, { hAlignContent: "left", className: styles.viewContent },
                        renderPrivilegeGroupMenu(componentRefs.current, allCheckboxes),
                        React.createElement(Vertical, { className: styles.rightPanel }, componentRefs.current.map((ref, index) => (React.createElement(Fragment, { key: `componentRefs_${index}` },
                            index === 0
                                ? renderRoleDescContent(ref)
                                : renderPrivilegesGroupContent(ref, index - 1),
                            index !==
                                componentRefs.current.length -
                                    1 && (React.createElement(Divider, { className: styles.rightPanelDivider })))))))),
                !isReviewSelection ? (React.createElement(Horizontal, { className: cx(styles.mainReviewContent) }, renderReviewScreen())) : null))));
};
//# sourceMappingURL=create-edit-form.container.js.map